import { useEffect, useState } from 'react'
import { useNotification } from '../contexts/NotificationContext'
import { formatCurrency, formatDateTime, formatEnergy, SIDE_LABELS } from '../utils/helpers'

function PositionsTable({ apiService, wsService }) {
  const [positions, setPositions] = useState([])
  const [summary, setSummary] = useState(null)
  const [pagination, setPagination] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [status, setStatus] = useState({ type: 'loading', message: 'Caricamento...' })
  const { showNotification } = useNotification()

  // Helper function for escaping HTML
  const escapeHtml = (text) => {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Helper function for side badge styling
  const getSideBadgeStyle = (side) => {
    switch (side?.toUpperCase()) {
      case 'BUY':
        return { backgroundColor: '#e8f5e8', color: '#2e7d32' }
      case 'SELL':
        return { backgroundColor: '#ffeaea', color: '#d32f2f' }
      default:
        return { backgroundColor: '#f0f0f0', color: '#666' }
    }
  }

  // Load positions from API
  const loadPositions = async () => {
    if (isLoading || !apiService || !apiService.isAuthenticated()) return

    setIsLoading(true)
    setStatus({ type: 'loading', message: 'Caricamento...' })

    try {
      const result = await apiService.getPositions(currentPage)
      console.log('API response for positions:', result)
      
      // Handle different response formats
      let rawPositions = []
      if (result.success && result.data) {
        rawPositions = result.data
      } else if (result.items) {
        rawPositions = result.items
      } else if (Array.isArray(result)) {
        rawPositions = result
      }
      
      // Map API response to expected format
      const positions = rawPositions.map(pos => ({
        id: pos.id,
        marketId: pos.marketId || 'DAM', // Default market
        instrument: pos.instrument || 'POWER_HOURLY',
        side: pos.side || pos.type?.toUpperCase() || 'BUY',
        qtyMWh: pos.qtyMWh || pos.quantity || 0,
        priceEURMWh: pos.priceEURMWh || pos.price || 0,
        valueEUR: pos.valueEUR || (pos.quantity * pos.price) || 0,
        settlementDate: pos.settlementDate || pos.timestamp,
        createdAt: pos.createdAt || pos.timestamp,
        status: pos.status
      }))
      
      console.log('Mapped positions:', positions)
      setPositions(positions)
      setSummary(result.summary)
      setPagination(result.pagination)
      setStatus({ type: 'connected', message: 'Dati aggiornati' })

    } catch (error) {
      console.error('Error loading positions:', error)
      setStatus({ type: 'disconnected', message: 'Errore caricamento' })
      showNotification(`Errore nel caricamento delle posizioni: ${error.message}`, 'error')
    } finally {
      setIsLoading(false)
    }
  }

  // Handle WebSocket updates
  const onPositionsUpdate = (positions) => {
    console.log('Received positions update via WebSocket:', positions)
    // Trigger a refresh when receiving updates
    loadPositions()
  }

  // Setup effects
  useEffect(() => {
    loadPositions()

    // Setup WebSocket listener if available
    if (wsService?.on) {
      wsService.on('positionsUpdate', onPositionsUpdate)
    }

    // Auto-refresh every 30 seconds
    const refreshInterval = setInterval(() => {
      if (!isLoading) {
        loadPositions()
      }
    }, 30000)

    return () => {
      clearInterval(refreshInterval)
      if (wsService?.off) {
        wsService.off('positionsUpdate', onPositionsUpdate)
      }
    }
  }, [currentPage])

  const handlePrevPage = () => {
    if (pagination?.hasPrev) {
      setCurrentPage(prev => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (pagination?.hasNext) {
      setCurrentPage(prev => prev + 1)
    }
  }

  const renderPositionRow = (position, index) => {
    const isAiGenerated = position.source === 'ai-agent'
    const borderLeft = isAiGenerated ? '4px solid #4caf50' : '4px solid transparent'
    const backgroundColor = isAiGenerated 
      ? (index % 2 === 0 ? '#f1f8e9' : '#e8f5e9') 
      : (index % 2 === 0 ? '#fafafa' : 'transparent')

    return (
      <tr 
        key={position.id || `position-${index}`}
        className="MuiTableRow-root" 
        style={{ 
          borderBottom: '1px solid #e0e0e0',
          borderLeft: borderLeft,
          backgroundColor: backgroundColor
        }}
      >
        {/* Source/AI Indicator */}
        <td className="MuiTableCell-root MuiTableCell-body" style={{ padding: '12px 16px' }}>
          {isAiGenerated ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '1.2rem' }}>🤖</span>
              <span 
                className="MuiChip-root" 
                style={{ 
                  backgroundColor: '#e8f5e9', 
                  color: '#2e7d32', 
                  padding: '4px 8px', 
                  borderRadius: '12px', 
                  fontSize: '0.75rem', 
                  fontWeight: '600',
                  border: '1px solid #4caf50'
                }}
              >
                AI AGENT
              </span>
            </div>
          ) : (
            <span 
              className="MuiChip-root" 
              style={{ 
                backgroundColor: '#e3f2fd', 
                color: '#1976d2', 
                padding: '4px 8px', 
                borderRadius: '12px', 
                fontSize: '0.75rem', 
                fontWeight: '500' 
              }}
            >
              MANUALE
            </span>
          )}
        </td>

        {/* Market Segment */}
        <td className="MuiTableCell-root MuiTableCell-body" style={{ padding: '12px 16px', color: '#333' }}>
          <span 
            className="MuiChip-root" 
            style={{ 
              backgroundColor: position.segment === 'DAM' ? '#fff3e0' : 
                            position.segment === 'IDM' ? '#f3e5f5' : '#e1f5fe', 
              color: position.segment === 'DAM' ? '#ef6c00' : 
                     position.segment === 'IDM' ? '#7b1fa2' : '#0277bd', 
              padding: '4px 8px', 
              borderRadius: '12px', 
              fontSize: '0.75rem', 
              fontWeight: '500' 
            }}
          >
            {position.segment || 'DAM'}
          </span>
        </td>

        {/* Type (Buy/Sell) */}
        <td className="MuiTableCell-root MuiTableCell-body" style={{ padding: '12px 16px' }}>
          <span 
            className="MuiChip-root" 
            style={{ 
              padding: '4px 8px', 
              borderRadius: '12px', 
              fontSize: '0.75rem', 
              fontWeight: '500',
              ...getSideBadgeStyle(position.type)
            }}
          >
            {position.type?.toUpperCase() === 'BUY' ? 'ACQUISTO' : 
             position.type?.toUpperCase() === 'SELL' ? 'VENDITA' : 
             position.type?.toUpperCase()}
          </span>
        </td>

        {/* Quantity */}
        <td className="MuiTableCell-root MuiTableCell-body" style={{ padding: '12px 16px', textAlign: 'right', color: '#333', fontWeight: '500' }}>
          {position.quantity ? `${position.quantity} MW` : 'N/A'}
        </td>

        {/* Price */}
        <td className="MuiTableCell-root MuiTableCell-body" style={{ padding: '12px 16px', textAlign: 'right', color: '#333', fontWeight: '500' }}>
          €{position.price ? position.price.toFixed(2) : '0.00'}/MWh
        </td>

        {/* Status */}
        <td className="MuiTableCell-root MuiTableCell-body" style={{ padding: '12px 16px' }}>
          <span 
            className="MuiChip-root" 
            style={{ 
              padding: '4px 8px', 
              borderRadius: '12px', 
              fontSize: '0.75rem', 
              fontWeight: '500',
              backgroundColor: position.status === 'active' ? '#e8f5e9' :
                             position.status === 'completed' ? '#e3f2fd' :
                             position.status === 'pending' ? '#fff3e0' : '#f5f5f5',
              color: position.status === 'active' ? '#2e7d32' :
                     position.status === 'completed' ? '#1976d2' :
                     position.status === 'pending' ? '#f57c00' : '#666'
            }}
          >
            {position.status?.toUpperCase() || 'UNKNOWN'}
          </span>
        </td>

        {/* Timestamp */}
        <td className="MuiTableCell-root MuiTableCell-body" style={{ padding: '12px 16px', color: '#666', fontSize: '0.875rem' }}>
          {formatDateTime(position.timestamp)}
        </td>

        {/* AI Reasoning (only for AI positions) */}
        <td className="MuiTableCell-root MuiTableCell-body" style={{ padding: '12px 16px', color: '#666', fontSize: '0.875rem', maxWidth: '200px' }}>
          {isAiGenerated && position.aiReasoning ? (
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '8px', 
              borderRadius: '6px', 
              border: '1px solid #e0e0e0',
              fontSize: '0.8rem'
            }}>
              <strong>🧠 AI:</strong> {position.aiReasoning}
            </div>
          ) : (
            isAiGenerated ? 'Posizione automatica' : 'Inserimento manuale'
          )}
        </td>
      </tr>
    )
  }

  return (
    <div className="MuiPaper-root MuiCard-root" style={{ padding: '24px', margin: '16px 0' }}>
      
      {/* Header with Title and Controls */}
      <div 
        className="MuiCardHeader-root" 
        style={{ 
          paddingBottom: '16px', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}
      >
        <div className="MuiCardHeader-content">
          <h2 className="MuiTypography-h5" style={{ margin: 0, color: '#1976d2' }}>
            Posizioni di Trading
          </h2>
          <p className="MuiTypography-body2" style={{ color: '#666', margin: '8px 0 0 0' }}>
            Panoramica delle posizioni attive sui mercati energetici
          </p>
        </div>
        
        <div className="positions-controls" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button 
            onClick={loadPositions}
            disabled={isLoading}
            className="MuiButton-root MuiButton-outlined MuiButton-outlinedPrimary"
            style={{ 
              border: '1px solid #1976d2', 
              color: '#1976d2', 
              padding: '8px 16px', 
              borderRadius: '4px', 
              background: 'transparent', 
              cursor: isLoading ? 'not-allowed' : 'pointer', 
              fontSize: '0.875rem' 
            }}
          >
            {isLoading ? 'Caricamento...' : 'Aggiorna'}
          </button>
          
          <div className="auto-refresh-status">
            <span 
              className={`connection-status MuiChip-root ${status.type}`}
              style={{ 
                padding: '4px 12px', 
                borderRadius: '16px', 
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <span 
                className="status-dot" 
                style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  display: 'inline-block', 
                  marginRight: '8px', 
                  backgroundColor: status.type === 'connected' ? '#4caf50' : status.type === 'disconnected' ? '#f44336' : '#ff9800'
                }}
              />
              <span className="status-text">{status.message}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div 
        className="MuiTableContainer-root" 
        style={{ border: '1px solid #e0e0e0', borderRadius: '4px', overflow: 'auto' }}
      >
        <table className="MuiTable-root" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead className="MuiTableHead-root" style={{ backgroundColor: '#f5f5f5' }}>
            <tr className="MuiTableRow-root MuiTableRow-head">
              <th className="MuiTableCell-root MuiTableCell-head" style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#333', borderBottom: '2px solid #e0e0e0' }}>
                🎯 Origine
              </th>
              <th className="MuiTableCell-root MuiTableCell-head" style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#333', borderBottom: '2px solid #e0e0e0' }}>
                📈 Mercato
              </th>
              <th className="MuiTableCell-root MuiTableCell-head" style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#333', borderBottom: '2px solid #e0e0e0' }}>
                🔄 Tipo
              </th>
              <th className="MuiTableCell-root MuiTableCell-head" style={{ padding: '16px', textAlign: 'right', fontWeight: '600', color: '#333', borderBottom: '2px solid #e0e0e0' }}>
                ⚡ Quantità
              </th>
              <th className="MuiTableCell-root MuiTableCell-head" style={{ padding: '16px', textAlign: 'right', fontWeight: '600', color: '#333', borderBottom: '2px solid #e0e0e0' }}>
                💰 Prezzo
              </th>
              <th className="MuiTableCell-root MuiTableCell-head" style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#333', borderBottom: '2px solid #e0e0e0' }}>
                📊 Status
              </th>
              <th className="MuiTableCell-root MuiTableCell-head" style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#333', borderBottom: '2px solid #e0e0e0' }}>
                🕐 Creato
              </th>
              <th className="MuiTableCell-root MuiTableCell-head" style={{ padding: '16px', textAlign: 'left', fontWeight: '600', color: '#333', borderBottom: '2px solid #e0e0e0' }}>
                🧠 Dettagli AI
              </th>
            </tr>
          </thead>
          <tbody className="MuiTableBody-root">
            {positions.length === 0 ? (
              <tr className="MuiTableRow-root" style={{ borderBottom: '1px solid #e0e0e0' }}>
                <td 
                  className="MuiTableCell-root MuiTableCell-body" 
                  colSpan={8} 
                  style={{ padding: '32px', textAlign: 'center', color: '#666' }}
                >
                  {isLoading ? 'Caricamento posizioni...' : 
                   !apiService?.isAuthenticated() ? 'Accedi per visualizzare le posizioni' :
                   'Nessuna posizione trovata'}
                </td>
              </tr>
            ) : (
              positions.map((position, index) => renderPositionRow(position, index))
            )}
          </tbody>
        </table>
      </div>

      {/* Summary Section */}
      {summary?.byMarketAndSide?.length > 0 && (
        <div 
          className="MuiAccordion-root" 
          style={{ 
            marginTop: '16px', 
            border: '1px solid #e0e0e0', 
            borderRadius: '4px' 
          }}
        >
          <div 
            className="MuiAccordionSummary-root" 
            style={{ 
              padding: '12px 16px', 
              backgroundColor: '#f8f9fa', 
              borderBottom: '1px solid #e0e0e0' 
            }}
          >
            <h3 className="MuiTypography-h6" style={{ margin: 0, color: '#333' }}>
              Riepilogo Portfolio
            </h3>
          </div>
          <div className="MuiAccordionDetails-root" style={{ padding: '16px' }}>
            <div className="summary-grid">
              <div className="summary-item" style={{ marginBottom: '8px' }}>
                <strong>Posizioni Totali:</strong> {summary.totalPositions}
              </div>
              {summary.byMarketAndSide.map((item, index) => (
                <div key={index} className="summary-item" style={{ marginBottom: '8px' }}>
                  <strong>{item.marketId} ({SIDE_LABELS[item.side] || item.side}):</strong>{' '}
                  {formatEnergy(item.totalQtyMWh, 'MWh')} @{' '}
                  {formatCurrency(item.avgPriceEURMWh)}/MWh ={' '}
                  {formatCurrency(item.totalValueEUR)}
                  <small> ({item.positionCount} posizioni)</small>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination?.totalPages > 1 && (
        <div 
          className="MuiTablePagination-root" 
          style={{ 
            padding: '16px', 
            borderTop: '1px solid #e0e0e0', 
            marginTop: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <button 
            onClick={handlePrevPage}
            disabled={!pagination.hasPrev}
            className="MuiButton-root MuiButton-text MuiButton-textPrimary"
            style={{ 
              color: pagination.hasPrev ? '#1976d2' : '#ccc', 
              border: 'none', 
              background: 'transparent', 
              cursor: pagination.hasPrev ? 'pointer' : 'not-allowed', 
              padding: '8px 16px', 
              borderRadius: '4px' 
            }}
          >
            ← Precedente
          </button>
          
          <span className="MuiTypography-body2" style={{ color: '#666' }}>
            Pagina {pagination.page} di {pagination.totalPages}
          </span>
          
          <button 
            onClick={handleNextPage}
            disabled={!pagination.hasNext}
            className="MuiButton-root MuiButton-text MuiButton-textPrimary"
            style={{ 
              color: pagination.hasNext ? '#1976d2' : '#ccc', 
              border: 'none', 
              background: 'transparent', 
              cursor: pagination.hasNext ? 'pointer' : 'not-allowed', 
              padding: '8px 16px', 
              borderRadius: '4px' 
            }}
          >
            Successiva →
          </button>
        </div>
      )}

    </div>
  )
}

export default PositionsTable
